import React from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import { GlTableCallbacks } from "../glDrillDown.lib";
import { ExpenseByVendorViewValueCell } from "./ExpenseByVendorViewValueCell";
import { ExpenseByVendorViewRow } from "./expenseByVendorViewTypes";
import styles from "./ExpenseByVendorTable.module.scss";

interface MakePeriodCellOptions {
    period: ReportPeriodWithLabel;
    periodIndex: number;
}

export const makeExpenseByVendorPeriodCell = ({
    period,
    periodIndex,
}: MakePeriodCellOptions) => {
    const ExpenseByVendorPeriodCell = ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ExpenseByVendorViewRow,
        unknown
    >): React.ReactElement => {
        const { values, vendorId, isSummary } = row.original;
        const onDrillDown = useViewTableMeta<GlTableCallbacks, unknown>(table)
            .callbacks?.onDrillDown;
        const value = values[periodIndex] ?? 0;

        // Synthetic Total row + "no vendor" rows can't drill down by counterparty.
        if (isSummary || vendorId === null) {
            return (
                <ExpenseByVendorSummaryValueCell
                    value={value}
                    period={period}
                    isSummary={isSummary}
                />
            );
        }

        return (
            <ExpenseByVendorViewValueCell
                value={value}
                period={period}
                vendorId={vendorId}
                onDrillDown={onDrillDown}
            />
        );
    };

    return React.memo(ExpenseByVendorPeriodCell);
};

interface ExpenseByVendorSummaryValueCellProps {
    value: number;
    period: ReportPeriodWithLabel;
    isSummary: boolean;
}

const ExpenseByVendorSummaryValueCell: React.FC<ExpenseByVendorSummaryValueCellProps> =
    React.memo(({ value, period, isSummary }) => {
        const { text, negativeClassName } = useReportAmountDisplay(value, {
            comparisonDiff: period.comparisonDiff,
        });

        return (
            <ViewTableCell
                className={classNames(styles.numericCell, negativeClassName, {
                    [styles.summaryCell]: isSummary,
                })}
            >
                {text}
            </ViewTableCell>
        );
    });
ExpenseByVendorSummaryValueCell.displayName = "ExpenseByVendorSummaryValueCell";
